/**
 * MakeHuman macro blending (port of its MacroModifier weighting): the body is a weighted sum of
 * targets named by gender, age, race, muscle, weight and height. Every macro slider is 0..1.
 */

export interface Macros {
  gender: number; // 0 female .. 1 male
  age: number; // 0 = 1 yr, 0.1875 = 11, 0.5 = 25, 1 = 90
  african: number; // race proportions, normalised to sum 1
  asian: number;
  caucasian: number;
  muscle: number; // 0.5 average
  weight: number; // 0.5 average
  height: number; // 0.5 average
}

export const DEFAULT_MACROS: Macros = { gender: 1, age: 0.5, african: 1, asian: 0, caucasian: 0, muscle: 0.5, weight: 0.5, height: 0.5 };

/** Convert between the age slider and years, using MakeHuman's piecewise mapping. */
export function ageToYears(a: number): number {
  if (a < 0.1875) return 1 + (a / 0.1875) * 10;
  if (a < 0.5) return 11 + ((a - 0.1875) / (0.5 - 0.1875)) * 14;
  return 25 + ((a - 0.5) / 0.5) * 65;
}
export function yearsToAge(y: number): number {
  if (y < 11) return ((y - 1) / 10) * 0.1875;
  if (y < 25) return 0.1875 + ((y - 11) / 14) * (0.5 - 0.1875);
  return 0.5 + ((y - 25) / 65) * 0.5;
}

type W = [string, number][];

function genderW(m: Macros): W { return [['female', 1 - m.gender], ['male', m.gender]]; }

function ageW(m: Macros): W {
  const a = m.age;
  if (a < 0.1875) return [['baby', 1 - a / 0.1875], ['child', a / 0.1875]];
  if (a < 0.5) { const t = (a - 0.1875) / (0.5 - 0.1875); return [['child', 1 - t], ['young', t]]; }
  const t = (a - 0.5) / 0.5;
  return [['young', 1 - t], ['old', t]];
}

/** min/average/max triplets for muscle and weight (0.5 = average). */
function triW(v: number, kind: 'muscle' | 'weight'): W {
  if (v < 0.5) return [[`min${kind}`, 1 - v / 0.5], [`average${kind}`, v / 0.5]];
  return [[`average${kind}`, 1 - (v - 0.5) / 0.5], [`max${kind}`, (v - 0.5) / 0.5]];
}

function raceW(m: Macros): W {
  const sum = m.african + m.asian + m.caucasian || 1;
  return [['african', m.african / sum], ['asian', m.asian / sum], ['caucasian', m.caucasian / sum]];
}

/** All macro target names with their weights for the given macro settings. */
export function macroTargetWeights(m: Macros): Map<string, number> {
  const out = new Map<string, number>();
  const add = (name: string, w: number) => { if (w > 1e-4) out.set(name, (out.get(name) ?? 0) + w); };
  const G = genderW(m), A = ageW(m), M = triW(m.muscle, 'muscle'), Wt = triW(m.weight, 'weight'), R = raceW(m);
  for (const [g, gw] of G) for (const [a, aw] of A) {
    for (const [r, rw] of R) add(`macro/${r}-${g}-${a}`, rw * gw * aw);
    for (const [mu, mw] of M) for (const [we, ww] of Wt) add(`macro/universal-${g}-${a}-${mu}-${we}`, gw * aw * mw * ww);
    // height: only average-build targets are shipped (see tools/build_human.py)
    const h = m.height;
    if (h < 0.5) add(`height/${g}-${a}-averagemuscle-averageweight-minheight`, gw * aw * (1 - h / 0.5));
    else add(`height/${g}-${a}-averagemuscle-averageweight-maxheight`, gw * aw * ((h - 0.5) / 0.5));
  }
  return out;
}
